'use client';

import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Track whether the initial getSession has already resolved loading
  const initializedRef = useRef(false);

  const fetchRole = async (userId: string): Promise<string | null> => {
    try {
      const supabase = createClient();
      console.log('[AuthContext][fetchRole] ▶ START — userId:', userId);

      const { data, error } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('[AuthContext][fetchRole] ✖ DB error:', error.message, '| code:', error.code);
        return null;
      }

      if (!data) {
        console.warn('[AuthContext][fetchRole] ⚠ No profile row found for userId:', userId);
        return null;
      }

      const fetchedRole = data?.role ?? null;
      console.log('[AuthContext][fetchRole] ✔ Raw DB row:', JSON.stringify(data));
      console.log('[AuthContext][fetchRole] ✔ Resolved role:', fetchedRole, '| userId:', userId);
      return fetchedRole;
    } catch (err) {
      console.error('[AuthContext][fetchRole] ✖ Exception:', err);
      return null;
    }
  };

  useEffect(() => {
    let active = true;
    const supabase = createClient();

    // Safety timer — if everything hangs, unblock after 8 seconds
    const safetyTimer = setTimeout(() => {
      if (active) {
        console.warn('[AuthContext] Safety timer triggered — forcing loading=false');
        setLoading(false);
      }
    }, 8000);

    // Resolve the role OUTSIDE the auth-state-change callback. Awaiting a Supabase
    // query inside onAuthStateChange holds the auth client's internal lock and can
    // deadlock every subsequent request (which is why pages hung on client-side
    // navigation but worked after a hard refresh). setTimeout(…, 0) defers the
    // query so it runs after the callback returns and the lock is released.
    const resolveRole = (userId: string) => {
      setTimeout(async () => {
        const r = await fetchRole(userId);
        if (active) {
          console.log('[AuthContext][resolveRole] ✔ role:', r, '| userId:', userId);
          setRole(r);
        }
      }, 0);
    };

    // IMPORTANT: this callback is intentionally NOT async and never awaits a
    // Supabase call. State is set synchronously; role is fetched via resolveRole().
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, sess) => {
      if (!active) return;
      console.log('[AuthContext][onAuthStateChange] ▶ event:', event, '| email:', sess?.user?.email ?? 'none');

      if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setRole(null);
        setLoading(false);
        return;
      }

      if (sess?.user) {
        setSession(sess);
        setUser(sess.user);
        resolveRole(sess.user.id);
      } else {
        setSession(null);
        setUser(null);
        setRole(null);
      }
      setLoading(false);
    });

    // Initial session check (awaiting here is safe — we're NOT inside the callback).
    supabase.auth
      .getSession()
      .then(({ data: { session: s }, error }) => {
        if (!active) return;
        if (error) {
          console.error('[AuthContext][getSession] ✖ Error:', error.message);
        } else if (s?.user) {
          console.log('[AuthContext][getSession] ✔ Session found — email:', s.user.email);
          setSession(s);
          setUser(s.user);
          resolveRole(s.user.id);
        } else {
          console.log('[AuthContext][getSession] ℹ No active session found');
          setSession(null);
          setUser(null);
          setRole(null);
        }
        initializedRef.current = true;
        setLoading(false);
        clearTimeout(safetyTimer);
      })
      .catch((err) => {
        if (!active) return;
        console.error('[AuthContext] Auth init error:', err);
        setLoading(false);
        clearTimeout(safetyTimer);
      });

    return () => {
      active = false;
      clearTimeout(safetyTimer);
      subscription?.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, metadata: { fullName?: string; phone?: string; avatarUrl?: string } = {}) => {
    const sb = createClient();
    const { data, error } = await sb.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          phone: metadata?.phone || '',
          avatar_url: metadata?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const sb = createClient();
    const { data, error } = await sb.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const sb = createClient();
    // Use local scope so logout doesn't depend on a successful server round-trip.
    // (Global scope throws when the access token is already expired/invalid, which
    // would leave the user "stuck" signed-in in the UI.)
    try {
      const { error } = await sb.auth.signOut({ scope: 'local' });
      if (error) {
        console.warn('[AuthContext][signOut] ⚠ signOut returned error (continuing):', error.message);
      }
    } catch (err) {
      console.warn('[AuthContext][signOut] ⚠ signOut threw (continuing):', err);
    } finally {
      // Always clear local auth state regardless of the network result.
      setSession(null);
      setUser(null);
      setRole(null);
    }
  };

  const getCurrentUser = async () => {
    const sb = createClient();
    const { data: { user: u }, error } = await sb.auth.getUser();
    if (error) {
      if (error.message.includes('refresh_token_not_found')) {
        await sb.auth.signOut();
        setSession(null);
        setUser(null);
        setRole(null);
      }
      throw error;
    }
    return u;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async () => {
    if (!user) return null;
    const sb = createClient();
    const { data, error } = await sb
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    role,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
